import { ComponentObservable, componentObserver } from '../../component-observer';
import { CheckoutResponse, deleteItemFromShoppingCart, submitOrder } from '../../api/shopping-cart-api';
import { toastNotification } from '../../building-blocks/toast-notification/toast-notification.module';

class EcatalogCheckoutModule implements ComponentObservable {
  /**
   * Selector of all e-catalog checkouts that must be initialized in javascript.
   */
  componentSelector = '.checkout-e-catalog';

  // Options that require serial numbers over 6000
  overSixthousandOptions = ['161076', '161077', '161097', '161099', '161672', '161725', '161726', '161727', '161876', '161958', '161965'];

  // Options that require serial numbers over 3000
  overThreethousandOptions = ['161078', '161079', '161090', '161098', '161100', '161673', '161757', '161758', '161759', '161877', '161959', '161969'];

  // Options that require serial numbers over 2000
  overTwothousandOptions = ['161080', '161081', '161908'];

  /**
   * Initialize all mega checkouts on the current page.
   * @param observe Boolean if the checkout module should listen for changes in the DOM and initialize dynamically added
   *   buttons
   */
  initializeAll(observe: boolean): void {
    const allEcatalogCheckouts = document.querySelectorAll(this.componentSelector);
    for (let i = 0; i < allEcatalogCheckouts.length; i++) {
      this.initialize(allEcatalogCheckouts[i] as HTMLElement);
    }

    if (observe) {
      this.startListening();
    }
  }

  /**
   * Listen for changes in DOM and initialize checkouts when new ones appear
   */
  startListening(): void {
    componentObserver.subscribeListener(this);
  }

  /**
   * Initialize the given checkout element.
   * @param checkout The checkout root element that should be initialized
   */
  initialize(checkout: HTMLElement): void {
    componentObserver.markElementAsInitialized(this.componentSelector, checkout);
    const serialInput = checkout.querySelector('#serial-input') as HTMLInputElement;
    const submitButton = checkout.querySelector('#submit-order') as HTMLInputElement;

    const removeItemFromCart = (event): void => {
      const currentTarget = event.currentTarget as HTMLElement;
      const itemId = currentTarget.dataset.id;
      deleteItemFromShoppingCart(itemId).then((success) => {
        currentTarget.closest('.shopping-cart-item').remove();
        if (!success && checkout.dataset.errorMessage) {
          const errorMessage = checkout.dataset.errorMessage;
          toastNotification.createErrorToastMessage(errorMessage);
        }
      });
    };

    submitButton.disabled = true;
    checkout.querySelector<HTMLElement>('#no-error').style.display = 'none';
    this.removeError(checkout, '#invalid-sn-error');
    this.removeError(checkout, '#missing-sn-error');
    this.removeError(checkout, '.range-error');

    const handleSerialInputKeypress = (): void => {
      serialInput.value = serialInput.value
        .replace(/[^,\d]/gi, '')
        .replace(/,{2,}/gi, ',')
        .replace(/^,/gi, '');
      this.updateQuantities(checkout, serialInput.value);
    };

    const handleSerialInputBlur = (): void => {
      this.checkParts(checkout, serialInput.value);
      if (serialInput.value.endsWith(',')) {
        serialInput.value = serialInput.value.slice(0, -1);
      }
      if (!/^[0-9,]*$/.test(serialInput.value) || this.hasInvalidSerialNumbers(serialInput.value)) {
        this.addError(checkout, '#invalid-sn-error');
      } else {
        this.removeError(checkout, '#invalid-sn-error');
        if (serialInput.value !== '') {
          this.removeError(checkout, '#missing-sn-error');
        }
      }

      if (serialInput.value === '') {
        this.addError(checkout, '#missing-sn-error');
      } else {
        this.removeError(checkout, '#missing-sn-error');
      }
      checkSubmitButton();
    };

    const checkSubmitButton = (): void => {
      let flag = false;
      if (serialInput.parentElement.classList.contains('form-control--invalid')) {
        flag = true;
      }
      if (serialInput.value === '') {
        flag = true;
      }
      if (checkout.querySelector<HTMLInputElement>('#customer-id-input').value === '') {
        flag = true;
      }
      submitButton.disabled = flag;
    };

    serialInput.addEventListener('change', handleSerialInputKeypress);
    serialInput.addEventListener('keypress', handleSerialInputKeypress);
    serialInput.addEventListener('keyup', handleSerialInputKeypress);
    serialInput.addEventListener('blur', handleSerialInputBlur);
    checkout.querySelector<HTMLInputElement>('#customer-id-input').addEventListener('blur', checkSubmitButton);
    checkout.addEventListener('keyup', checkSubmitButton);
    submitButton.addEventListener('click', this.submitOrder);

    const deleteItemButtons = checkout.querySelectorAll('.shopping-cart-item__remove-item-button');
    for (let i = 0; i < deleteItemButtons.length; i++) {
      deleteItemButtons[i].addEventListener('click', removeItemFromCart);
    }
  }

  submitOrder(event: MouseEvent): void {
    const button = event.currentTarget as HTMLInputElement;
    button.disabled = true;
    const serialNumbers = document.querySelector<HTMLInputElement>('#serial-input').value;
    const customerId = document.querySelector<HTMLInputElement>('#customer-id-input').value;
    const note = document.querySelector<HTMLInputElement>('#note-input').value;
    const licenseType = document.querySelector<HTMLInputElement>('input[name="licence-options"]:checked').value;

    submitOrder(serialNumbers, licenseType, note, customerId).then((checkoutResponse: CheckoutResponse) => {
      if (checkoutResponse.errors && checkoutResponse.errors.length > 0) {
        checkoutResponse.errors.forEach((error) => {
          toastNotification.createErrorToastMessage(error);
        });
      }
      if (checkoutResponse.success) {
        window.location.href = document.querySelector<HTMLInputElement>('#redirect-address').value;
      }
    });
  }

  /**
   * Checks if the serial input field contains invalid serial numbers
   * @param value The value from the serial numbers input field
   * @returns boolean result of the check
   */
  hasInvalidSerialNumbers(value: string): boolean {
    const sns = value.split(',').map((sn) => parseInt(sn));

    if (Math.max(...sns) > 2147483647) {
      return true;
    }
    return false;
  }

  /**
   * Updates the quantities of each product based on the number of valid serial numbers
   * @param checkout the root element of the checkout
   * @param value The value from the serial numbers input field
   */
  updateQuantities(checkout: HTMLElement, value: string): void {
    if (value.endsWith(',')) {
      value = value.slice(0, -1);
    }
    const sns = value.split(',');
    const validSns = sns.map((sn) => parseInt(sn)).filter((n) => !isNaN(n));
    const quantities = checkout.querySelectorAll('.shopping-cart-item__product-quantity > span');

    for (let i = 0; i < quantities.length; i++) {
      quantities[i].innerHTML = validSns.length.toString();
    }
    this.updateTotal(checkout);
  }

  updateTotal(checkout: HTMLElement): void {
    let totalPrice = 0;
    const licenseInput = checkout.querySelector('input[name="licence-options"]:checked') as HTMLInputElement;

    if (licenseInput.value === '0') {
      checkout.querySelectorAll('.shopping-cart-item__product-quantity').forEach((e: HTMLElement) => {
        const quantity = parseInt(e.querySelector('span').innerHTML);
        const price = parseFloat(
          e.parentElement.querySelector<HTMLElement>('.shopping-cart-item__product-price').dataset.price
        );
        totalPrice += quantity * price;
      });
    }

    const formatter = new Intl.NumberFormat(this.getLocale(), { style: 'currency', currency: 'EUR' });
    const priceString = formatter.formatToParts(totalPrice);

    priceString.forEach((e) => {
      if (e.type === 'group') {
        e.value = ' ';
      }
      if (e.type === 'currency') {
        e.value = '';
      }
    });

    checkout.querySelector('#total').innerHTML = priceString.map((e) => e.value).join('');
  }

  getLocale(): string {
    const locales = [
      'en',
      'de',
      'fr',
      'it',
      'pt',
      'zh',
      'en_US',
      'en_AE',
      'en_EG',
      'en_SA',
      'en_ZA',
      'en_GB',
      'en_IN',
      'en_PK',
      'en_AU',
      'de_CH',
      'fr_CH',
      'fr_FR',
      'it_CH',
      'de_DE',
      'de_AT',
      'en_CA',
      'fr_CA',
      'zh_CN',
      'pt_BR',
      'ru',
      'ru_RU',
      'tr',
      'ja',
      'ja_JP',
      'es',
      'es_CL',
      'es_CO',
      'es_EC',
      'es_MX',
      'es_PE',
      'es_ES'
    ];
    const uri = window.location.pathname;
    const uriParts = uri.split('/');

    const locale = uriParts.filter((value) => -1 !== locales.indexOf(value));
    if (locale.length === 0) {
      return 'en';
    }
    return locale[0].replace('_', '-');
  }

  addError(root: HTMLElement, errorContainerSelector: string): void {
    const serialInput = root.querySelector('#serial-input') as HTMLInputElement;
    serialInput.setAttribute('aria-invalid', 'true');

    if (!serialInput.parentElement.classList.contains('form-control--invalid')) {
      serialInput.parentElement.classList.add('form-control--invalid');
      root.querySelector<HTMLElement>('#no-error').style.display = 'none';
    }
    root.querySelector<HTMLElement>(errorContainerSelector).style.display = 'block';
  }

  removeError(root: HTMLElement, errorContainerSelector: string): void {
    const serialInput = root.querySelector('#serial-input') as HTMLInputElement;
    root.querySelector<HTMLElement>(errorContainerSelector).style.display = 'none';
    if (
      root.querySelectorAll<HTMLElement>('.checkout-error:not([style*="display: none"]):not([style*="display:none"])')
        .length === 0
    ) {
      serialInput.setAttribute('aria-invalid', 'true');
      root.querySelector<HTMLElement>('#no-error').style.display = 'block';
      if (serialInput.parentElement.classList.contains('form-control--invalid')) {
        serialInput.parentElement.classList.remove('form-control--invalid');
      }
    }
  }

  checkParts(root: HTMLElement, value: string): void {
    const serialInput = root.querySelector<HTMLInputElement>('#serial-input');
    const partNumberContainers = root.querySelectorAll('.shopping-cart-item__product-id');
    let rangeError = root.querySelectorAll<HTMLElement>('.range-error');

    for (let i = 1; i < rangeError.length; i++) {
      rangeError[i].parentElement.removeChild(rangeError[i]);
    }

    for (let i = 0; i < partNumberContainers.length; i++) {
      const partNumber = partNumberContainers[i].textContent;
      const serialNumbers = value.split(',');
      let displayPn = false;

      if (this.overSixthousandOptions.indexOf(partNumber) > -1) {
        displayPn = true;
        for (let i = 0; i < serialNumbers.length; i++) {
          const min = Math.min(...serialNumbers.map((e) => parseInt(e)));
          if (min <= 6000 && displayPn) {
            const newRangeError = rangeError[0].cloneNode(true) as HTMLElement;
            newRangeError.querySelector<HTMLElement>('.part-number-info').textContent = partNumber;
            newRangeError.querySelector<HTMLElement>('.larger-than-info').textContent = '6000';
            newRangeError.style.display = 'block';
            rangeError[0].parentElement.appendChild(newRangeError);
            displayPn = false;
          }
        }
      }

      if (this.overThreethousandOptions.indexOf(partNumber) > -1) {
        displayPn = true;
        for (let i = 0; i < serialNumbers.length; i++) {
          const min = Math.min(...serialNumbers.map((e) => parseInt(e)));
          if (min <= 3000 && displayPn) {
            const newRangeError = rangeError[0].cloneNode(true) as HTMLElement;
            newRangeError.querySelector<HTMLElement>('.part-number-info').textContent = partNumber;
            newRangeError.querySelector<HTMLElement>('.larger-than-info').textContent = '3000';
            newRangeError.style.display = 'block';
            rangeError[0].parentElement.appendChild(newRangeError);
            displayPn = false;
          }
        }
      }

      if (this.overTwothousandOptions.indexOf(partNumber) > -1) {
        displayPn = true;
        for (let i = 0; i < serialNumbers.length; i++) {
          const min = Math.min(...serialNumbers.map((e) => parseInt(e)));
          if (min <= 2000 && displayPn) {
            const newRangeError = rangeError[0].cloneNode(true) as HTMLElement;
            newRangeError.querySelector<HTMLElement>('.part-number-info').textContent = partNumber;
            newRangeError.querySelector<HTMLElement>('.larger-than-info').textContent = '2000';
            newRangeError.style.display = 'block';
            rangeError[0].parentElement.appendChild(newRangeError);
            displayPn = false;
          }
        }
      }
    }

    rangeError = root.querySelectorAll<HTMLElement>('.range-error');
    if (rangeError.length > 1) {
      if (!serialInput.parentElement.classList.contains('form-control--invalid')) {
        serialInput.parentElement.classList.add('form-control--invalid');
        root.querySelector<HTMLElement>('#no-error').style.display = 'none';
      }
    }
  }
}

const ecatalogCheckoutModule = new EcatalogCheckoutModule();
export { ecatalogCheckoutModule as ecatalogCheckout };
